import React, { useEffect, useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView, SafeAreaView, Platform } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@cubemeet/core/services/supabase';
import { getTravelBackgroundAssetNoFallback, ModuleHeader } from '@cubemeet/ui';
import { MdVisibility, MdFavorite, MdInsertComment, MdLocationOn, MdSearch } from 'react-icons/md';
import { useTravelI18n } from '../modules/travel/useTravelI18n';

interface GlobalPost {
  id: string;
  author_id: string;
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  view_count: number;
  like_count: number;
  comment_count: number;
  created_at: string;
}

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const { t } = useTravelI18n();

  const [posts, setPosts] = useState<GlobalPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    try {
      setLoading(true);
      setHasSearched(true);

      // Search logic: title contains query OR tags contains query
      // Note: Supabase 'contains' for array columns, 'ilike' for text
      // We might need to do two queries or use 'or' syntax carefully
      
      // Using .or() with filter syntax
      // tags.cs.{query} checks if tags array contains the query string (exact match for tag)
      // title.ilike.%{query}% checks if title contains the query string (case insensitive)
      
      // Constructing the OR filter
      // We want: (status = 'published') AND (title ILIKE %query% OR tags CS {query})
      // Supabase JS client .or() combines with previous filters using AND by default if chained, 
      // but inside .or() it's OR.
      
      // However, mixing array contains and text ilike in a single OR string can be tricky.
      // Let's try a simpler approach first: search by title OR search by tag if it looks like a tag.
      // Or just use the text search on title/summary/tags if we had a full text search index.
      // Without FTS, we can try:
      
      const { data, error } = await supabase
        .from('travel_posts')
        .select('*')
        .eq('status', 'published')
        .or(`title.ilike.%${searchQuery}%,tags.cs.{${searchQuery}}`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Search failed:', error);
        // Fallback: if the OR syntax with array fails, try just title search
        const { data: titleData, error: titleError } = await supabase
            .from('travel_posts')
            .select('*')
            .eq('status', 'published')
            .ilike('title', `%${searchQuery}%`)
            .order('created_at', { ascending: false })
            .limit(50);
            
        if (!titleError) {
            setPosts(titleData || []);
        }
        return;
      }

      setPosts(data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.mainContainer}>
        <ModuleHeader
          moduleType="travel"
          title={t('search.title', { default: '搜索' })}
          showBack
          onBack={() => navigate(-1)}
        />

        <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainer}>
          <View style={styles.headerSection}>
            <Text style={styles.queryText}>
              {t('search.resultsFor', { default: '搜索结果' })}: <Text style={styles.highlight}>#{query}</Text>
            </Text>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#ff6b35" />
            </View>
          ) : posts.length > 0 ? (
            <View style={styles.listContainer}>
              {posts.map((post) => {
                const resolveCoverSource = () => {
                  if (!post.cover_image) return undefined;
                  const assetSource = getTravelBackgroundAssetNoFallback(post.cover_image);
                  if (assetSource) return assetSource;
                  return { uri: post.cover_image };
                };
                const coverSource = resolveCoverSource();

                return (
                  <TouchableOpacity
                    key={post.id}
                    style={styles.postCard}
                    onPress={() => navigate(`/travel-post/${post.id}`)}
                    activeOpacity={0.7}
                  >
                    {coverSource && (
                      <Image source={coverSource} style={styles.coverImage} />
                    )}
                    <View style={styles.postContent}>
                      <Text style={styles.postTitle} numberOfLines={2}>{post.title}</Text>
                      {post.summary && (
                        <Text style={styles.postSummary} numberOfLines={2}>{post.summary}</Text>
                      )}
                      <View style={styles.postMeta}>
                        <View style={styles.statsRow}>
                          <View style={styles.statItem}>
                            <MdVisibility size={14} color="#999" />
                            <Text style={styles.statText}>{post.view_count}</Text>
                          </View>
                          <Text style={styles.statDivider}>·</Text>
                          <View style={styles.statItem}>
                            <MdFavorite size={14} color="#999" />
                            <Text style={styles.statText}>{post.like_count}</Text>
                          </View>
                          <Text style={styles.statDivider}>·</Text>
                          <View style={styles.statItem}>
                            <MdInsertComment size={14} color="#999" />
                            <Text style={styles.statText}>{post.comment_count}</Text>
                          </View>
                        </View>
                        {post.city && (
                          <View style={styles.locationRow}>
                            <MdLocationOn size={14} color="#0ea5a4" />
                            <Text style={styles.cityText}>{post.city}</Text>
                          </View>
                        )}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : (
            <View style={styles.emptyContainer}>
              <MdSearch size={48} color="#ccc" />
              <Text style={styles.emptyText}>
                {hasSearched ? t('search.noResults', { default: '未找到相关内容' }) : t('search.startSearching', { default: '开始搜索' })}
              </Text>
            </View>
          )}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        minHeight: '100vh',
      },
      default: {
        elevation: 4,
      }
    }),
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 40,
  },
  headerSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  queryText: {
    fontSize: 16,
    color: '#333',
  },
  highlight: {
    color: '#ff6b35',
    fontWeight: 'bold',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  listContainer: {
    padding: 16,
  },
  postCard: {
    marginBottom: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#f0f0f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  coverImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f0f0f0',
  },
  postContent: {
    padding: 12,
  },
  postTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  postSummary: {
    fontSize: 13,
    color: '#666',
    lineHeight: 20,
    marginBottom: 8,
  },
  postMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    fontSize: 12,
    color: '#999',
  },
  statDivider: {
    fontSize: 12,
    color: '#ccc',
    marginHorizontal: 6,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  cityText: {
    fontSize: 12,
    color: '#0ea5a4',
    fontWeight: '500',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 80,
    gap: 16,
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
  },
});

export default SearchPage;
